import React, { useEffect, useState, useCallback } from 'react';
import { listTaskTemplates, createTaskTemplate, activateTaskTemplate, deactivateTaskTemplate, saveStageTaskTemplates, listStagesForType } from '../api';
import { useConfirm } from './ConfirmProvider';

const DEFAULT_STAGES = [
  'Fact Finding & Strategy',
  'Action & Drafting',
  'Negotiation & Resolution',
  'Court/Formal Proceedings',
  'Post-Resolution/Closure'
];

const caseTypeOptions = [
  'Family','Criminal','Civil','Immigration','Litigation','Real_Estate','Corporate','Contract','Property','Intellectual_Property','Transactional','Advisory','Arbitration','Mediation','Conveyancing','Other'
];

export default function TaskTemplatesManager({ authAxios, permissions, showFormMessage }) {
  const confirm = useConfirm();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ case_type: 'Litigation', case_stage: '' });
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', default_due_days_offset: 2 });
  const [createBusy, setCreateBusy] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [stageOptions, setStageOptions] = useState(DEFAULT_STAGES);
  const [stagesLoading, setStagesLoading] = useState(false);

  const canManage = permissions?.isPartner || permissions?.isAssociate || permissions?.isSuperuser;

  const fetchStages = useCallback(async (caseType) => {
    if (!authAxios) return;
    setStagesLoading(true);
    try {
      const data = await listStagesForType(authAxios, caseType);
      const stages = data.stages || DEFAULT_STAGES;
      setStageOptions(stages);
      setFilters(f => ({ ...f, case_stage: stages[0] || '' }));
    } catch (e) {
      console.error('Failed to load stages', e);
      setStageOptions(DEFAULT_STAGES);
      setFilters(f => ({ ...f, case_stage: DEFAULT_STAGES[0] }));
    } finally {
      setStagesLoading(false);
    }
  }, [authAxios]);

  useEffect(() => { fetchStages(filters.case_type); }, [filters.case_type, fetchStages]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const data = await listTaskTemplates(authAxios, filters);
      setTemplates(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      showFormMessage && showFormMessage('error', 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTemplates(); }, [filters.case_type, filters.case_stage]);

  const filtered = templates.filter(t => {
    if (!search) return true;
    const q = search.toLowerCase();
    return t.title?.toLowerCase().includes(q) || t.description?.toLowerCase().includes(q);
  });

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title) return;
    setCreateBusy(true);
    try {
      await createTaskTemplate(authAxios, { ...form, case_type: filters.case_type, case_stage: filters.case_stage });
      setForm({ title: '', description: '', default_due_days_offset: 2 });
      setShowForm(false);
      fetchTemplates();
      showFormMessage && showFormMessage('success', 'Template created');
    } catch (err) {
      console.error(err);
      showFormMessage && showFormMessage('error', 'Failed to create template');
    } finally {
      setCreateBusy(false);
    }
  };

  const toggleActive = async (tpl) => {
    setTogglingId(tpl.id);
    try {
      if (tpl.is_active) {
        await deactivateTaskTemplate(authAxios, tpl.id);
      } else {
        await activateTaskTemplate(authAxios, tpl.id);
      }
      fetchTemplates();
    } catch (err) {
      console.error(err);
      showFormMessage && showFormMessage('error', 'Update failed');
    } finally {
      setTogglingId(null);
    }
  };

  const handleSaveFromCase = async () => {
    const caseId = await confirm({ title: 'Save from Case', message: 'Enter Case ID to save current stage tasks as templates (will overwrite).', confirmText: 'Save', variant: 'primary', promptMode: true, promptLabel: 'Case ID', required: true });
    if (!caseId) return;
    try {
      await saveStageTaskTemplates(authAxios, caseId, true);
      fetchTemplates();
      showFormMessage && showFormMessage('success', 'Templates saved from case');
    } catch (err) {
      console.error(err);
      showFormMessage && showFormMessage('error', 'Failed saving from case');
    }
  };

  const activeCount = templates.filter(t => t.is_active).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Task Templates</h2>
          <p className="text-sm text-gray-500">{templates.length} templates &middot; {activeCount} active</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {canManage && (
            <>
              <button onClick={handleSaveFromCase} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm">
                Save From Case
              </button>
              <button
                onClick={() => { setShowForm(!showForm); setForm({ title: '', description: '', default_due_days_offset: 2 }); }}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
              >
                {showForm ? 'Cancel' : '+ New Template'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Filters row */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Case Type</label>
            <select className="border rounded px-3 py-2 text-sm" value={filters.case_type} onChange={e => setFilters(f => ({ ...f, case_type: e.target.value }))}>
              {caseTypeOptions.map(ct => <option key={ct} value={ct}>{ct.replace('_', ' ')}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Case Stage</label>
            <select className="border rounded px-3 py-2 text-sm" value={filters.case_stage} onChange={e => setFilters(f => ({ ...f, case_stage: e.target.value }))} disabled={stagesLoading}>
              {stagesLoading ? <option>Loading stages...</option> : stageOptions.map(st => <option key={st} value={st}>{st}</option>)}
            </select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-gray-500 mb-1">Search</label>
            <input
              type="text"
              placeholder="Filter by title or description..."
              className="border rounded px-3 py-2 text-sm w-full"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Create form (collapsible) */}
      {showForm && canManage && (
        <form onSubmit={handleCreate} className="bg-white rounded-lg shadow p-6">
          <h3 className="font-bold text-lg mb-4">New Template</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
              <input className="border rounded p-2 w-full" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Review discovery documents" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Due Offset (days)</label>
              <input type="number" min="0" className="border rounded p-2 w-full" value={form.default_due_days_offset} onChange={e => setForm(f => ({ ...f, default_due_days_offset: parseInt(e.target.value)||0 }))} />
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea className="border rounded p-2 w-full h-20" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Optional details about this task template..." />
          </div>
          <div className="flex justify-end mt-4">
            <button disabled={createBusy || !form.title} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm disabled:opacity-50">
              {createBusy ? 'Creating...' : 'Create Template'}
            </button>
          </div>
        </form>
      )}

      {/* Template cards */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          {templates.length === 0 ? 'No templates for this stage yet.' : 'No templates match your search.'}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(t => (
            <div key={t.id} className={`bg-white rounded-lg shadow p-4 border-l-4 ${t.is_active ? 'border-green-500' : 'border-gray-300'}`}>
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-semibold text-gray-800">{t.title}</h4>
                <span className={`px-2 py-0.5 text-xs rounded-full ${t.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  {t.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              {t.description && (
                <p className="text-sm text-gray-600 mb-3 line-clamp-3">{t.description}</p>
              )}
              <div className="flex items-center justify-between mt-auto pt-3 border-t border-gray-100">
                <span className="text-xs text-gray-400">Due in {t.default_due_days_offset ?? 2} days</span>
                {canManage && (
                  <button
                    disabled={togglingId === t.id}
                    onClick={() => toggleActive(t)}
                    className={`px-3 py-1 text-xs rounded ${t.is_active ? 'bg-red-100 text-red-700 hover:bg-red-200' : 'bg-green-100 text-green-700 hover:bg-green-200'} disabled:opacity-50`}
                  >
                    {togglingId === t.id ? '...' : t.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
